import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Shield, Star, Award, Pencil } from 'lucide-react'
import type { AccountBadge } from '@olimhub/shared'
import { SYSTEM_BADGE_SLUGS } from '@olimhub/shared'

interface BadgePillProps {
  accountBadge: AccountBadge
  size?: 'sm' | 'md'
}

function FilledCircle({ bg, children }: { bg: string; children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-flex',
      width: '1em',
      height: '1em',
      borderRadius: '50%',
      background: bg,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}>
      {children}
    </span>
  )
}

function getBadgeIcon(slug: string, level: number, px: number): React.ReactNode {
  const inner = Math.round(px * 0.65)
  switch (slug) {
    case 'super_admin':
      return (
        <FilledCircle bg="#6184d8">
          <Shield size={inner} color="#fff" strokeWidth={2.5} />
        </FilledCircle>
      )
    case 'admin':
      return <Shield size={px} color="#E8A838" strokeWidth={2} />
    case 'pioneer':
      return level >= 2
        ? (
          <FilledCircle bg="#F59E0B">
            <Star size={inner} color="#fff" strokeWidth={2.5} />
          </FilledCircle>
        )
        : <Star size={px} color="#7C3AED" strokeWidth={2} />
    case SYSTEM_BADGE_SLUGS.GROUP_ADMIN:
      return (
        <FilledCircle bg="#6184d8">
          <Shield size={inner} color="#fff" strokeWidth={2.5} />
        </FilledCircle>
      )
    case SYSTEM_BADGE_SLUGS.GROUP_EDITOR:
      return <Pencil size={px} color="var(--text-muted)" strokeWidth={2} />
    case 'community_superhero':
      return (
        <svg width={px} height={px} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.663 13 12 21.892 3.337 13V6c0-.552 3.448-4 4-4h9.326c.552 0 4 3.448 4 4z" fill="#fff915" stroke="#800000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="m11.657 16.16c-1.236 0-2.243-.097-3.02-.292C7.873 15.662 4.287 14.138 3.925 13.976l.834-1.77c.388.174 3.926 1.676 4.565 1.828.639.151 1.416.227 2.333.227.972 0 1.652-.119 2.041-.357.389-.249.583-.579.583-.99 0-.325-.118-.59-.354-.796-.236-.195-1.673-.506-2.131-.679-.458-.173-1.034-.352-1.729-.536-.666-.195-1.305-.417-1.916-.666-.597-.26-1.083-.595-1.458-1.007-.375-.411-.562-.952-.562-1.623 0-.682.208-1.266.625-1.753.458-.487 1.055-.86 1.819-1.12C9.338 4.442 10.254 4.307 11.31 4.307c.93 0 3.818.012 4.749.428.219-.219 3.485 1.316 4.219 1.737l-.853 1.754c-.422-.487-3.415-1.472-3.865-1.64-1.344-.407-3.423-.38-4.103-.38-.805 0-1.41.098-1.812.293-.389.194-.583.503-.583.925 0 .303.118.552.354.747.236.195.555.368.958.52.403.14.854.281 1.354.422.958.27 2.853.667 3.492.96.653.292 1.146.66 1.479 1.104.347.433.521 1.012.521 1.737 0 .682-.208 1.266-.625 1.753-.416.487-1.041.86-1.874 1.12-.82.25-1.84.374-3.062.374z" fill="#800000" />
        </svg>
      )
    default:
      return <Award size={px} color="var(--text-muted)" />
  }
}

export function BadgePill({ accountBadge, size = 'md' }: BadgePillProps) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const wrapRef = useRef<HTMLSpanElement>(null)

  const px = size === 'sm' ? 16 : 20
  const { badge, level, levelData } = accountBadge
  const tooltipText =
    levelData && levelData.name !== badge.name
      ? `${badge.name} · ${levelData.name}`
      : badge.name

  const updateCoords = () => {
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect()
      setCoords({ top: rect.top, left: rect.left + rect.width / 2 })
    }
  }

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('touchstart', close)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('touchstart', close)
    }
  }, [open])

  const tooltip = open
    ? createPortal(
        <div
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform: 'translateX(-50%) translateY(calc(-100% - 6px))',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: '4px 8px',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-xs)',
            fontWeight: 500,
            color: 'var(--text-strong)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 9999,
            boxShadow: 'var(--shadow-modal)',
          }}
        >
          {tooltipText}
        </div>,
        document.body,
      )
    : null

  return (
    <>
      <span
        ref={wrapRef}
        style={{ display: 'inline-flex', alignItems: 'center', fontSize: px, flexShrink: 0, cursor: 'default' }}
        onMouseEnter={() => { updateCoords(); setOpen(true) }}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => { e.stopPropagation(); updateCoords(); setOpen((v) => !v) }}
      >
        {getBadgeIcon(badge.slug, level, px)}
      </span>
      {tooltip}
    </>
  )
}
